import { CalendarDate, DateValue, now, today } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { DateTime, Settings } from 'luxon';
import { useMemo } from 'react';

import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import DateRangePicker, {
  DateRangePickerProps,
} from 'design-system/components/DatePicker/DateRangePicker';
import { InputSizes } from 'design-system/mixins/input';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { Iso8601DateString } from 'lib/types';

export interface DatePickerProps
  extends Pick<DateRangePickerProps, 'granularity' | 'hideTimeZone'> {
  handleDatesChange: (range: RangeValue<DateValue> | null) => void;
  dateRange: DateRange;
  disabled?: boolean;
  id?: string;
  dataTest?: string;
  hidePresets?: boolean;
  label?: string | null;
  minDate?: CalendarDate;
  helpText?: string;
  size?: InputSizes;
  className?: string;
  showClear?: boolean;
}

export interface DateRange {
  startDate: Iso8601DateString | undefined;
  endDate: Iso8601DateString | undefined;
}

export default function DashboardDatePicker({
  dateRange,
  handleDatesChange,
  minDate = new CalendarDate(2018, 1, 1),
  hidePresets = false,
  label = `Dates (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
  granularity = 'day',
  hideTimeZone = true,
  className,
  showClear = true,
  ...props
}: DatePickerProps) {
  const { startDate, endDate } = dateRange;

  const dateRangeLuxon = useMemo<AnalyticsDateRange>(
    () => ({
      startDate: startDate ? DateTime.fromISO(startDate) : undefined,
      endDate: endDate ? DateTime.fromISO(endDate) : undefined,
    }),
    [startDate, endDate],
  );

  return (
    <DateRangePicker
      dateRange={dateRangeLuxon}
      onChange={handleDatesChange}
      hidePresets={hidePresets}
      label={label}
      minDate={minDate}
      maxDate={
        granularity === 'day'
          ? today(Settings.defaultZone.name)
          : now(Settings.defaultZone.name).set({
              hour: 23,
              minute: 59,
              second: 59,
              millisecond: 999,
            })
      }
      showClear={showClear}
      onClear={() => handleDatesChange(null)}
      granularity={granularity}
      hideTimeZone={hideTimeZone}
      className={className}
      {...props}
    />
  );
}
